"use client";

import { useState, useMemo, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Calculator, Sparkles, CircleCheck } from "lucide-react";
declare global {
  interface Window {
    Calendly?: {
      initPopupWidget: (options: {
        url: string;
      }) => void;
    };
  }
}
const aidSchedule: Record<string, Record<string, number>> = {
  "pompe-chaleur-air-eau": {
    bleu: 5000,
    jaune: 4000,
    violet: 3000,
    rose: 0
  },
  "pompe-chaleur-geothermique": {
    bleu: 11000,
    jaune: 9000,
    violet: 6000,
    rose: 0
  },
  "chaudiere-biomasse": {
    bleu: 10000,
    jaune: 8000,
    violet: 4000,
    rose: 0
  },
  "isolation-murs": {
    bleu: 25,
    jaune: 20,
    violet: 15,
    rose: 0
  },
  "isolation-combles": {
    bleu: 25,
    jaune: 20,
    violet: 15,
    rose: 0
  },
  "isolation-toiture": {
    bleu: 75,
    jaune: 60,
    violet: 40,
    rose: 0
  },
  "vmc-double-flux": {
    bleu: 2500,
    jaune: 2000,
    violet: 1500,
    rose: 0
  }
};
const ceeSchedule: Record<string, number> = {
  "pompe-chaleur-air-eau": 3500,
  "pompe-chaleur-geothermique": 4000,
  "chaudiere-biomasse": 3000,
  "isolation-murs": 20,
  "isolation-combles": 18,
  "isolation-toiture": 25,
  "vmc-double-flux": 2000
};
const typicalSurfaces: Record<string, number> = {
  "isolation-murs": 100,
  "isolation-combles": 50,
  "isolation-toiture": 80
};
const ecoPretAmounts: Record<string, number> = {
  single: 15000,
  double: 25000,
  triple: 30000,
  global: 50000
};
const questions = [{
  id: "propertyType",
  emoji: "📍",
  question: "Parlez-nous de votre logement",
  prompt: "Quel type de logement souhaitez-vous rénover ?",
  options: [{
    value: "maison",
    label: "Maison individuelle"
  }, {
    value: "appartement",
    label: "Appartement"
  }, {
    value: "immeuble",
    label: "Immeuble collectif"
  }]
}, {
  id: "incomeCategory",
  emoji: "💰",
  question: "Quelle est votre situation financière ?",
  prompt: "Sélectionnez selon votre revenu fiscal de référence",
  options: [{
    value: "bleu",
    label: "MaPrimeRénov' Bleu (revenus très modestes)"
  }, {
    value: "jaune",
    label: "MaPrimeRénov' Jaune (revenus modestes)"
  }, {
    value: "violet",
    label: "MaPrimeRénov' Violet (revenus intermédiaires)"
  }, {
    value: "rose",
    label: "MaPrimeRénov' Rose (revenus supérieurs)"
  }]
}, {
  id: "equipmentType",
  emoji: "🔧",
  question: "Quel projet de rénovation envisagez-vous ?",
  prompt: "Choisissez le type de travaux",
  options: [{
    value: "pompe-chaleur-air-eau",
    label: "Pompe à chaleur air/eau"
  }, {
    value: "pompe-chaleur-geothermique",
    label: "Pompe à chaleur géothermique"
  }, {
    value: "chaudiere-biomasse",
    label: "Chaudière biomasse"
  }, {
    value: "isolation-murs",
    label: "Isolation des murs (par m²)"
  }, {
    value: "isolation-combles",
    label: "Isolation des combles (par m²)"
  }, {
    value: "isolation-toiture",
    label: "Isolation de la toiture (par m²)"
  }, {
    value: "vmc-double-flux",
    label: "VMC double flux"
  }]
}, {
  id: "location",
  emoji: "🗺️",
  question: "Où se situe votre logement ?",
  prompt: "Sélectionnez votre localisation",
  options: [{
    value: "idf",
    label: "Île-de-France"
  }, {
    value: "province",
    label: "Province"
  }, {
    value: "outremer",
    label: "Outre-mer"
  }]
}];
export default function AperçuDesAidesFinancières() {
  const {
    ref: cardRef,
    isVisible: cardVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const [showResults, setShowResults] = useState(false);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [fadingOut, setFadingOut] = useState(false);
  useEffect(() => {
    const link = document.createElement("link");
    link.href = "https://assets.calendly.com/assets/external/widget.css";
    link.rel = "stylesheet";
    document.head.appendChild(link);
    const script = document.createElement("script");
    script.src = "https://assets.calendly.com/assets/external/widget.js";
    script.async = true;
    document.body.appendChild(script);
    return () => {
      if (link.parentNode) {
        link.parentNode.removeChild(link);
      }
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);
  const calculatedAids = useMemo(() => {
    if (!answers.equipmentType || !answers.incomeCategory) {
      return {
        maPrimeRenov: 0,
        cee: 0,
        ecoPret: 0,
        taxCredit: 0,
        total: 0
      };
    }
    let maPrimeRenov = aidSchedule[answers.equipmentType]?.[answers.incomeCategory] || 0;
    let cee = ceeSchedule[answers.equipmentType] || 0;
    if (answers.equipmentType.startsWith("isolation-")) {
      const surface = typicalSurfaces[answers.equipmentType] || 1;
      maPrimeRenov = maPrimeRenov * surface;
      cee = cee * surface;
    }
    const ecoPret = ecoPretAmounts.single;
    const estimatedWorkCost = (maPrimeRenov + cee) * 2;
    const taxCredit = Math.min(2000, Math.round((estimatedWorkCost - maPrimeRenov - cee) * 0.3));
    const total = maPrimeRenov + cee + ecoPret + taxCredit;
    return {
      maPrimeRenov,
      cee,
      ecoPret,
      taxCredit,
      total
    };
  }, [answers.equipmentType, answers.incomeCategory]);
  const handleAnswer = (questionId: string, value: string) => {
    setFadingOut(true);
    setTimeout(() => {
      setAnswers(prev => ({
        ...prev,
        [questionId]: value
      }));
      setStep(prev => prev + 1);
      setFadingOut(false);
    }, 300);
  };
  const handleCalculate = () => {
    setShowResults(true);
  };
  const handleReset = () => {
    setShowResults(false);
    setStep(0);
    setAnswers({});
    setFadingOut(false);
  };
  const handleCalendlyClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (window.Calendly) {
      window.Calendly.initPopupWidget({
        url: "https://calendly.com/brice-betravaux/30min"
      });
    }
  };
  const getAnswerLabel = (questionId: string) => {
    const question = questions.find(q => q.id === questionId);
    const option = question?.options.find(opt => opt.value === answers[questionId]);
    return option?.label || "";
  };
  const isFormComplete = step >= questions.length;
  return <section className="relative min-h-screen w-full overflow-hidden bg-[hsl(210,12%,25%)] flex items-center justify-center py-16 px-4">
      {}
      <div className="absolute inset-0 z-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1442601/img/modern_renovated_home_exterior.png)] bg-cover bg-center opacity-20" style={{
      transform: "translateY(calc(var(--scroll, 0) * -0.2))",
      willChange: "transform"
    }} />

      {}
      <div className="absolute top-8 right-8 opacity-10 pointer-events-none z-0">
        <Calculator className="size-24 text-[hsl(215,90%,56%)]" />
      </div>

      {}
      <div className="relative z-10 w-full flex items-center justify-center">
        <Card ref={cardRef} className={`w-full max-w-2xl min-h-[600px] bg-white shadow-strong transition-all duration-[var(--duration-slow)] ease-[var(--ease-out-back)] ${cardVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"} ${cardVisible ? "hover:translate-y-[-4px]" : ""}`}>
          <CardHeader className="space-y-2 border-b border-[hsl(210,12%,80%)] pb-6">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Sparkles className="size-6 text-[hsl(35,94%,53%)]" />
              <CardTitle className="text-3xl font-display text-[hsl(0,0%,12%)] text-center">
                Simulateur MaPrimeRénov' 2025
              </CardTitle>
            </div>
            <p className="text-sm text-[hsl(210,12%,50%)] text-center">
              Découvrez en quelques clics le montant des aides disponibles pour votre projet de rénovation
            </p>
          </CardHeader>

          <CardContent className="space-y-8 pt-6">
            {!showResults ? <>
                {}
                {step > 0 && <div className="space-y-3">
                    {questions.slice(0, step).map((q, i) => <div key={q.id} data-index={i} className="flex items-center gap-3 p-3 rounded-lg bg-[hsl(215,90%,95%)] border border-[hsl(215,90%,85%)] opacity-60">
                        <CircleCheck className="size-5 text-[hsl(215,90%,56%)] shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-[hsl(210,12%,50%)] mb-0.5">
                            {q.emoji} {q.question}
                          </p>
                          <p className="text-sm font-semibold text-[hsl(0,0%,12%)] truncate">
                            {getAnswerLabel(q.id)}
                          </p>
                        </div>
                      </div>)}
                  </div>}

                {}
                {step < questions.length && <div className={`transition-all duration-300 ease-[var(--ease-smooth)] ${fadingOut ? "opacity-0 translate-y-4" : "opacity-100 translate-y-0"}`}>
                    <label className="block text-lg font-semibold text-[hsl(0,0%,12%)] mb-2">
                      {questions[step].emoji} {questions[step].question}
                    </label>
                    <p className="text-sm text-[hsl(210,12%,50%)] mb-4">
                      {questions[step].prompt}
                    </p>
                    <div className="space-y-3">
                      {questions[step].options.map((option, i) => <Button key={option.value} data-index={i} onClick={() => handleAnswer(questions[step].id, option.value)} variant="outline" className="w-full justify-start text-left h-auto py-4 px-4 border-2 border-[hsl(210,12%,80%)] hover:border-[hsl(215,90%,56%)] hover:bg-[hsl(215,90%,95%)] transition-all duration-[var(--duration-fast)] text-[hsl(0,0%,12%)] font-medium">
                          {option.label}
                        </Button>)}
                    </div>
                  </div>}

                {}
                {isFormComplete && <div className={`transition-all duration-300 ease-[var(--ease-smooth)] ${fadingOut ? "opacity-0 translate-y-4" : "opacity-100 translate-y-0"}`}>
                    <Button onClick={handleCalculate} className="w-full bg-[hsl(35,94%,53%)] hover:bg-[hsl(35,90%,60%)] active:bg-[hsl(35,90%,48%)] text-white font-semibold py-6 text-base shadow-medium transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] hover:shadow-strong hover:-translate-y-1 active:translate-y-0 mt-6">
                      <Calculator className="size-5" />
                      Calculer mes aides maintenant
                    </Button>
                  </div>}
              </> : <>
                {}
                <div className="space-y-4 animate-in slide-in-from-bottom duration-[var(--duration-medium)]">
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <Sparkles className="size-5 text-[hsl(35,94%,53%)]" />
                    <h3 className="text-xl font-display font-semibold text-[hsl(0,0%,12%)]">
                      Voici vos aides estimées pour 2025
                    </h3>
                  </div>

                  {}
                  <div className="p-4 rounded-lg bg-[hsl(215,90%,95%)] border-l-4 border-[hsl(215,90%,56%)] hover:shadow-medium transition-all duration-[var(--duration-medium)]">
                    <p className="text-sm text-[hsl(210,12%,50%)] mb-1">MaPrimeRénov' 2025</p>
                    <p className="text-2xl font-display font-bold text-[hsl(215,90%,56%)]">
                      {calculatedAids.maPrimeRenov.toLocaleString('fr-FR')} €
                    </p>
                  </div>

                  {}
                  {calculatedAids.cee > 0 && <div className="p-4 rounded-lg bg-[hsl(215,90%,95%)] border-l-4 border-[hsl(215,90%,56%)] hover:shadow-medium transition-all duration-[var(--duration-medium)]">
                      <p className="text-sm text-[hsl(210,12%,50%)] mb-1">Certificats d'Économie d'Énergie (CEE)</p>
                      <p className="text-2xl font-display font-bold text-[hsl(215,90%,56%)]">
                        {calculatedAids.cee.toLocaleString('fr-FR')} €
                      </p>
                      <p className="text-xs text-[hsl(210,12%,50%)] mt-1">
                        Prime énergie versée par les fournisseurs d'énergie
                      </p>
                    </div>}

                  {}
                  <div className="p-4 rounded-lg bg-[hsl(215,90%,95%)] border-l-4 border-[hsl(215,90%,56%)] hover:shadow-medium transition-all duration-[var(--duration-medium)]">
                    <p className="text-sm text-[hsl(210,12%,50%)] mb-1">Éco-prêt à taux zéro</p>
                    <p className="text-2xl font-display font-bold text-[hsl(215,90%,56%)]">
                      {calculatedAids.ecoPret.toLocaleString('fr-FR')} €
                    </p>
                    <p className="text-xs text-[hsl(210,12%,50%)] mt-1">
                      Prêt sans intérêts pour financer vos travaux
                    </p>
                  </div>

                  {}
                  {calculatedAids.taxCredit > 0 && <div className="p-4 rounded-lg bg-[hsl(215,90%,95%)] border-l-4 border-[hsl(215,90%,56%)] hover:shadow-medium transition-all duration-[var(--duration-medium)]">
                      <p className="text-sm text-[hsl(210,12%,50%)] mb-1">Crédit d'impôt estimé</p>
                      <p className="text-2xl font-display font-bold text-[hsl(215,90%,56%)]">
                        {calculatedAids.taxCredit.toLocaleString('fr-FR')} €
                      </p>
                    </div>}

                  {}
                  <div className="p-6 rounded-lg bg-[hsl(35,94%,95%)] border-2 border-[hsl(35,94%,53%)] mt-4 hover:shadow-strong transition-all duration-[var(--duration-medium)]">
                    <p className="text-sm text-[hsl(210,12%,50%)] mb-2 text-center">Total des aides disponibles</p>
                    <p className="text-4xl font-display font-bold text-[hsl(35,94%,53%)] text-center">
                      {calculatedAids.total.toLocaleString('fr-FR')} €
                    </p>
                    <p className="text-sm text-[hsl(210,12%,50%)] mt-3 text-center">
                      💡 C'est le moment idéal pour lancer votre projet de rénovation !
                    </p>
                  </div>

                  {}
                  <div className="p-3 rounded-md bg-[hsl(210,12%,97%)] border border-[hsl(210,12%,80%)] mt-4">
                    <p className="text-xs text-[hsl(210,12%,50%)]">
                      * Estimation basée sur le barème 2025 MaPrimeRénov' et CEE. Les montants réels peuvent varier 
                      selon votre situation, la composition de votre foyer et le coût des travaux. 
                      Contactez un conseiller Be Travaux pour une estimation personnalisée et un accompagnement complet.
                    </p>
                  </div>
                </div>

                {}
                <div className="flex flex-col sm:flex-row gap-3 mt-6">
                  <Button onClick={handleReset} variant="outline" className="flex-1 border-[hsl(210,12%,80%)] hover:bg-[hsl(210,12%,97%)] transition-all duration-[var(--duration-medium)]">
                    Nouvelle simulation
                  </Button>
                  <Button onClick={handleCalendlyClick} className="flex-1 bg-[hsl(215,90%,56%)] hover:bg-[hsl(215,85%,62%)] active:bg-[hsl(215,85%,50%)] text-white font-semibold shadow-medium transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] hover:shadow-strong hover:-translate-y-1 active:translate-y-0">Prendre rendez-vous avec un expert</Button>
                </div>
              </>}
          </CardContent>
        </Card>
      </div>
    </section>;
}